<script setup lang="ts">
import type Charger from '@/classes/charger_classes/charger'
import type FavouriteLocation from '@/classes/favouriteLocation'
import {
  GettersTypes,
  MutationTypes,
  SecondaryDialogContent,
  type State,
} from '@/store/store_types'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import OutlinedBtn from '../ui-elements/buttons/OutlinedBtn.vue'
import TextBtn from '../ui-elements/buttons/TextBtn.vue'
import LocationDisplayCard from './LocationDisplayCard.vue'

const store = useStore<State>()

const savedChargers = computed<Charger[]>(() => store.getters[GettersTypes.getSavedChargers])
const savedLocations = computed(() => store.state.favLocations)

const searchQuery = ref('')

const filteredSavedChargers = computed(() => {
  if (searchQuery.value) {
    return savedChargers.value.filter((charger) => {
      if (charger.name?.toLowerCase().includes(searchQuery.value.toLowerCase().trim())) return true
      if (charger.address?.toLowerCase().includes(searchQuery.value.toLowerCase().trim()))
        return true
      if (
        charger.bookableEVSEDisplayData.some((evse) =>
          evse.display_name?.includes(searchQuery.value.toLowerCase().trim()),
        )
      )
        return true
      return false
    })
  }
  return savedChargers.value
})

const filteredSavedLocations = computed(() => {
  if (searchQuery.value) {
    return savedLocations.value.filter((location) => {
      if (location.name?.toLowerCase().includes(searchQuery.value.toLowerCase().trim())) return true
      if (location.address?.toLowerCase().includes(searchQuery.value.toLowerCase().trim()))
        return true
      return false
    })
  }
  return savedLocations.value
})

const openNetworkSettings = () => {
  store.commit(MutationTypes.setSecondaryDialogContent, SecondaryDialogContent.NETWORK_SETTINGS)
}

const navigateToChargerListView = () => {
  store.commit(MutationTypes.setDashboardDialogContent, 'saved-charger-list')
}

const navigateToChargerDetails = (charger: Charger) => {
  store.commit(MutationTypes.setSelectedCharger, charger.id)
  store.commit(MutationTypes.setDashboardDialogContent, 'saved-charger-details')
}

const navigateToFavLocationsListView = () => {
  store.commit(MutationTypes.setDashboardDialogContent, 'saved-location-list')
}

const navigateToFavouriteLocationDetails = (location: FavouriteLocation) => {
  store.commit(MutationTypes.setSelectedFavLocation, location)
  store.commit(MutationTypes.setDashboardDialogContent, 'saved-location-details')
}
</script>
<template>
  <v-container class="px-5 bg-pwt_snow">
    <!-- Action buttons -->
    <v-card-text class="d-flex align-center justify-end pb-0">
      <TextBtn :padded="false" @click="openNetworkSettings"> Settings/filters </TextBtn>
      <TextBtn :padded="false"> Browse charging options on map </TextBtn>
    </v-card-text>
    <!-- Search bar -->
    <v-card-text class="mb-5">
      <v-text-field
        v-model="searchQuery"
        clearable
        class="bg-pwt_snow"
        placeholder="Search saved charging stations & locations..."
        hide-details
      ></v-text-field>
    </v-card-text>
    <!-- Saved chargers section -->
    <v-card-title class="mb-3"> Saved chargers </v-card-title>
    <v-card-text v-if="!filteredSavedChargers.length && searchQuery">
      No results found for '{{ searchQuery }}'
    </v-card-text>
    <v-card-text v-else-if="!filteredSavedChargers.length"> No saved chargers </v-card-text>
    <v-card-text v-else>
      <v-row no-gutters>
        <v-col
          cols="12"
          md="6"
          v-for="(charger, index) in filteredSavedChargers.slice(0, searchQuery ? undefined : 4)"
          :key="charger.id"
          :class="index % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-md-2'"
          class="mb-3"
        >
          <LocationDisplayCard
            :location="charger"
            @click="() => navigateToChargerDetails(charger)"
          />
        </v-col>
      </v-row>
      <v-row v-if="!searchQuery" no-gutters justify="end">
        <OutlinedBtn
          @click="navigateToChargerListView"
          :disabled="filteredSavedChargers.length <= 4"
        >
          See more...
        </OutlinedBtn>
      </v-row>
    </v-card-text>
    <!-- Saved locations section -->
    <v-card-title class="mb-3"> Saved locations </v-card-title>
    <v-card-text v-if="!filteredSavedLocations.length && searchQuery">
      No results found for '{{ searchQuery }}'
    </v-card-text>
    <v-card-text v-else-if="!filteredSavedLocations.length"> No saved locations </v-card-text>
    <v-card-text v-else>
      <v-row no-gutters>
        <v-col
          cols="12"
          md="6"
          v-for="(favLocation, index) in filteredSavedLocations.slice(
            0,
            searchQuery ? undefined : 4,
          )"
          :key="favLocation.localId"
          :class="index % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-md-2'"
          class="mb-3"
        >
          <LocationDisplayCard
            :location="favLocation"
            @click="() => navigateToFavouriteLocationDetails(favLocation)"
          />
        </v-col>
      </v-row>
      <v-row v-if="!searchQuery" no-gutters justify="end">
        <OutlinedBtn
          @click="navigateToFavLocationsListView"
          :disabled="filteredSavedLocations.length <= 4"
        >
          See more...
        </OutlinedBtn>
      </v-row>
    </v-card-text>
  </v-container>
</template>
